import {
  Box,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  styled,
  TextField,
} from "@mui/material";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Check, Monitor } from "react-feather";
import { AmtInputAdornment } from "../../../../../components";
import { DeskInventoryFiltersGroupedByType } from "../../../domain";

function useResizeObserver<T extends HTMLElement>(
  ref: React.RefObject<T>,
  cb: () => void
) {
  useLayoutEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(cb);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [cb, ref]);
}

const measureCtx =
  typeof document === "undefined"
    ? null
    : document.createElement("canvas").getContext("2d");

function measureTextWidth(text: string, font: string) {
  if (!measureCtx) return text.length * 8;
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

type FilterItem = { id: string; label: string; deskIds: string[] };
type InventoryGroup = { type: string; items: FilterItem[] };

const DeskInventorySelectorContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    flexGrow: 1,
    alignItems: "center",
  },
}));

const StyledTextField = styled(TextField)({
  flex: "1 1 0",
  minWidth: 0,
  "& .MuiSelect-select": {
    minWidth: 0,
  },
});

type RenderValueProps = {
  ids: string[];
  labelFor: (id: string) => string;
};
const RenderSelectedValue: React.FC<RenderValueProps> = ({ ids, labelFor }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(ids.length);

  const recalc = useCallback(() => {
    if (!ref.current) return;
    const parent = ref.current.parentElement as HTMLElement | null;
    if (!parent) return;

    const containerWidth = parent.clientWidth;

    const style = getComputedStyle(parent);
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    let used = 0;
    let show = 0;

    for (let i = 0; i < ids.length; i++) {
      const txt = (i ? ", " : "") + labelFor(ids[i]);
      const txtWidth = measureTextWidth(txt, font);

      const remaining = ids.length - (i + 1);
      const suffix = remaining > 0 ? ` … +${remaining}` : "";
      const suffixWidth = remaining > 0 ? measureTextWidth(suffix, font) : 0;

      if (used + txtWidth + suffixWidth > containerWidth) break;
      used += txtWidth;
      show = i + 1;
    }

    if (show === 0 && ids.length > 0) show = 1;

    setVisibleCount(show);
  }, [ids, labelFor]);

  useLayoutEffect(recalc, [recalc]);
  useResizeObserver(ref, recalc);

  if (ids.length === 0) return <>Oprema</>;

  const shownIds = ids.slice(0, visibleCount);
  const hidden = ids.length - visibleCount;

  return (
    <span
      ref={ref}
      title={ids.map(labelFor).join(", ")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        width: "100%",
        minWidth: 0,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {shownIds.map((id, i) => (
          <span key={id}>
            {i ? ", " : ""}
            {labelFor(id)}
          </span>
        ))}
      </span>

      {hidden > 0 && (
        <span style={{ flex: "none", marginLeft: 4 }}> +{hidden}</span>
      )}
    </span>
  );
};

export interface DeskInventorySelectorProps {
  assets: DeskInventoryFiltersGroupedByType[];
  selectedIds: string[];
  onToggle: (filterId: string) => void;
}

const makeId = (type: string, name: string, quantity: number) =>
  `${type}::${name}::${quantity}`;

export const DeskInventorySelector: React.FC<DeskInventorySelectorProps> = ({
  assets,
  selectedIds,
  onToggle,
}) => {
  const inventory: InventoryGroup[] = useMemo(() => {
    return (assets ?? [])
      .map((group) => {
        const type = group.type ?? "Other";
        const items: FilterItem[] = (group.filters ?? [])
          .map((f) => ({
            id: makeId(type, f.name, f.quantity),
            label: `${f.quantity}x ${f.name} (${f.deskIds.length})`,
            deskIds: f.deskIds ?? [],
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return { type, items };
      })
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [assets]);

  const labelFor = useCallback(
    (id: string) =>
      inventory.flatMap((g) => g.items).find((it) => it.id === id)?.label ?? id,
    [inventory]
  );

  return (
    <DeskInventorySelectorContainer>
      <Box sx={{ flexGrow: 1, display: "flex", minWidth: 0 }}>
        <AmtInputAdornment placement="start">
          <Monitor size={16} />
        </AmtInputAdornment>

        <StyledTextField
          className="MuiInput-leftAdornment"
          fullWidth
          select
          value={selectedIds}
          disabled={inventory.length < 1}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selected) => (
              <RenderSelectedValue
                ids={selected as string[]}
                labelFor={labelFor}
              />
            ),
          }}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {inventory.flatMap(({ type, items }) => [
            <ListSubheader key={type}>{type}</ListSubheader>,
            items.map((it) => {
              const selected = selectedIds.includes(it.id);
              return (
                <MenuItem
                  key={it.id}
                  value={it.id}
                  onClick={() => onToggle(it.id)}
                >
                  {selected && (
                    <ListItemIcon>
                      <Check size={16} />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={it.label} />
                </MenuItem>
              );
            }),
          ])}
        </StyledTextField>
      </Box>
    </DeskInventorySelectorContainer>
  );
};
